import { useState, useEffect, useCallback } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  ActionButtonsRow,
  Result,
  MathDiv,
  CopyHTMLButton,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
} from '../../../styles/global'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
import { roundToDecimals } from '../../../utils/numberFormat'

export function ConcreteIsolatedFooting() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [ap, setAp] = useState('')
  const [bp, setBp] = useState('')
  const [nk, setNk] = useState('')
  const [mk, setMk] = useState('')
  const [sigma, setSigma] = useState('')
  const [gammaf, setGammaf] = useState(1.4)
  const [fyk, setFyk] = useState(500)
  const [dl, setDl] = useState('')
  const [A, setA] = useState('')
  const [B, setB] = useState('')
  const [h, setH] = useState('')
  const [h0, setH0] = useState('')

  const [result, setResult] = useState('')
  const [data, setData] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const HISTORY_KEY = 'concrete-isolated-footing-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setAp(entry.ap)
      setBp(entry.bp)
      setNk(entry.nk)
      setMk(entry.mk)
      setSigma(entry.sigma)
      setGammaf(entry.gammaf || 1.4)
      setFyk(entry.fyk)
      setDl(entry.dl)
      setA(entry.A || '')
      setB(entry.B || '')
      setH(entry.h || '')
      setH0(entry.h0 || '')
      setResult(entry.result)
      setData(entry.data)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value, isOptional = false) => {
    if (value === '' || value === null || value === undefined) {
      return isOptional ? null : 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if ((name !== 'mk') & (numValue <= 0)) {
      return 'Valor deve ser maior que 0'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.ap = validateField('ap', ap)
    newErrors.bp = validateField('bp', bp)
    newErrors.nk = validateField('nk', nk)
    newErrors.mk = validateField('mk', mk)
    newErrors.sigma = validateField('sigma', sigma)
    newErrors.gammaf = validateField('gammaf', gammaf)
    newErrors.fyk = validateField('fyk', fyk)
    newErrors.dl = validateField('dl', dl)
    newErrors.A = validateField('A', A)
    newErrors.B = validateField('B', B)
    newErrors.h = validateField('h', h)
    newErrors.h0 = validateField('h0', h0)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [ap, bp, nk, mk, sigma, gammaf, fyk, dl, A, B, h, h0, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const requestData = {
        ap: Number(ap),
        bp: Number(bp),
        nk: Number(nk),
        mk: Number(mk),
        sigma: Number(sigma),
        gammaf: Number(gammaf),
        fyk: Number(fyk),
        dl: Number(dl),
        A: Number(A),
        B: Number(B),
        h: Number(h),
        h0: Number(h0),
      }

      setData(requestData)

      const response = await api.post('/concrete/isolated-footing', requestData)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        ap,
        bp,
        nk,
        mk,
        sigma,
        gammaf,
        fyk,
        dl,
        A,
        B,
        h,
        h0,
        result: response.data,
        data: requestData,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      ap,
      bp,
      nk,
      mk,
      sigma,
      gammaf,
      fyk,
      dl,
      A,
      B,
      h,
      h0,
      result,
      data,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Sapata Isolada.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)
        setAp(jsonData.ap)
        setBp(jsonData.bp)
        setNk(jsonData.nk)
        setMk(jsonData.mk)
        setSigma(jsonData.sigma)
        setGammaf(jsonData.gammaf || 1.4)
        setFyk(jsonData.fyk)
        setDl(jsonData.dl)
        setA(jsonData.A || '')
        setB(jsonData.B || '')
        setH(jsonData.h || '')
        setH0(jsonData.h0 || '')
        setResult(jsonData.result)
        setData(jsonData.data)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
    })
  }

  const baseReturn = result?.baseReturn
  const extraReturn = result?.extraReturn

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
        <div className="header-content">
          <BackButton to="/concreto">
            <FiArrowLeft /> Voltar para Concreto
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/concreto">Concreto</a>
            <span>/</span>
            <span>Sapata Isolada</span>
          </div>
          <h1>Sapata Isolada</h1>
          <p className="subtitle">
            Dimensionamento de sapatas isoladas de concreto armado com carga centrada ou excêntrica
          </p>
          <div className="badges">
            <Badge>NBR 6118:2014</Badge>
            <Badge>NBR 6122:2022</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #f3f0ff 0%, #e9e0ff 100%)"
        $border="2px solid #d4c5f9"
        $primaryColor="#764ba2"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta realiza o dimensionamento de sapatas isoladas de concreto armado, considerando
              carregamentos com ou sem excentricidade.
            </p>
            <p>
              O cálculo considera dois casos: excentricidade dentro do núcleo central (compressão total) e
              excentricidade fora do núcleo central (compressão parcial).
            </p>
            <div className="norms">
              <span>NBR 6118:2014</span>
              <span>NBR 6122:2022</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}

          <form onSubmit={handleCalculate}>
            <h3>Materiais</h3>
            <InputDiv>
              <span>Resistência Característica do Aço (MPa)</span>
              <ValidatedInput
                name="fyk"
                type="number"
                value={fyk || ''}
                onChange={(event) => setFyk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Geometria</h3>
            <InputDiv>
              <span>Dimensão da sapata na direção x (cm)</span>
              <ValidatedInput
                name="A"
                type="number"
                value={A}
                onChange={(event) => setA(event.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Dimensão da sapata na direção y (cm)</span>
              <ValidatedInput
                name="B"
                type="number"
                value={B}
                onChange={(event) => setB(event.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Altura total (cm)</span>
              <ValidatedInput
                name="h"
                type="number"
                value={h}
                onChange={(event) => setH(event.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Altura do rodapé (cm)</span>
              <ValidatedInput
                name="h0"
                type="number"
                value={h0}
                onChange={(event) => setH0(event.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Dimensão do pilar na direção x (cm)</span>
              <ValidatedInput
                name="ap"
                type="number"
                value={ap}
                onChange={(event) => setAp(event.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Dimensão do pilar na direção y (cm)</span>
              <ValidatedInput
                name="bp"
                type="number"
                value={bp}
                onChange={(event) => setBp(event.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Distância da barra tracionada à face tracionada (cm)</span>
              <ValidatedInput
                name="dl"
                type="number"
                value={dl}
                onChange={(event) => setDl(event.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>

            <h3>Esforços</h3>
            <InputDiv>
              <span>Vertical Característica (kN)</span>
              <ValidatedInput
                name="nk"
                type="number"
                value={nk}
                onChange={(event) => setNk(event.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Momento Característico (kN.cm)</span>
              <ValidatedInput
                name="mk"
                type="number"
                value={mk}
                onChange={(event) => setMk(event.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Coeficiente de ponderação γf</span>
              <ValidatedInput
                name="gammaf"
                type="number"
                value={gammaf || ''}
                onChange={(event) => setGammaf(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Solo</h3>
            <InputDiv>
              <span>Tensão admissível do solo (kN/m²)</span>
              <ValidatedInput
                name="sigma"
                type="number"
                value={sigma}
                onChange={(event) => setSigma(event.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>

        {result !== '' && baseReturn && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              {extraReturn && (
                <>
                  {baseReturn.eccentricityWithinLimit ? (
                    <>
                      <h3>Cálculo das Tensões</h3>
                      <MathDiv>
                        <BlockMath math={`\\sigma_{max} = ${extraReturn.sigmaMax.toFixed(2)} \\text{ kN/m}^2`} />
                        <BlockMath math={`\\sigma_{min} = ${extraReturn.sigmaMin.toFixed(2)} \\text{ kN/m}^2`} />
                      </MathDiv>
                      {extraReturn.sigmaResist ? (
                        <>
                          <MathDiv>
                            <BlockMath math={`\\sigma_{max} \\leq \\sigma_{adm}`} />
                          </MathDiv>
                          <h3>Cálculo do Momento</h3>
                          <MathDiv>
                            <BlockMath math={`M_{max} = ${extraReturn.mMax.toFixed(2)} \\text{ kN.m}`} />
                            <BlockMath
                              math={`M_{sd} = M_{max} \\cdot \\gamma_f = ${extraReturn.msd.toFixed(2)} \\text{ kN.m}`}
                            />
                          </MathDiv>
                          <h3>Cálculo da Armadura</h3>
                          <MathDiv>
                            <BlockMath
                              math={`A_s = \\dfrac{M_{sd}}{0,85 \\cdot d \\cdot f_{yd}} = ${extraReturn.as.toFixed(2)} \\text{ cm}^2 \\text{/m}`}
                            />
                          </MathDiv>
                        </>
                      ) : (
                        <>
                          <MathDiv>
                            <BlockMath math={`\\sigma_{max} > \\sigma_{adm}`} />
                          </MathDiv>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <h3>Cálculo das Tensões</h3>
                      <MathDiv>
                        <BlockMath math={`\\sigma_{min} = 0 \\text{ kN/m}^2`} />
                        <BlockMath math={`\\sigma_{max} = ${roundToDecimals(extraReturn.sigmaMax)} \\text{ kN/m}^2`} />
                      </MathDiv>
                      {extraReturn.sigmaResist ? (
                        <>
                          <MathDiv>
                            <BlockMath math={`\\sigma_{max} \\leq \\sigma_{adm}`} />
                          </MathDiv>
                          <h3>Cálculo do Momento</h3>
                          <MathDiv>
                            <BlockMath math={`M_{max} = ${roundToDecimals(extraReturn.mMax)} \\text{ kN.m}`} />
                            <BlockMath
                              math={`M_{sd} = M_{max} \\cdot \\gamma_f = ${roundToDecimals(extraReturn.msd)} \\text{ kN.m}`}
                            />
                          </MathDiv>
                          <h3>Cálculo da Armadura</h3>
                          <MathDiv>
                            <BlockMath
                              math={`A_s = \\dfrac{M_{sd}}{0,85 \\cdot d \\cdot f_{yd}} = ${extraReturn.as.toFixed(2)} \\text{ cm}^2 \\text{/m}`}
                            />
                          </MathDiv>
                        </>
                      ) : (
                        <>
                          <MathDiv>
                            <BlockMath math={`\\sigma_{max} > \\sigma_{adm}`} />
                          </MathDiv>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <h3>Peso próprio da sapata</h3>
              <MathDiv>
                <BlockMath
                  math={`\\boxed{G_{sapata} = ${roundToDecimals(baseReturn.weightCalculate.weight)} \\text{ kN}}`}
                />
              </MathDiv>
              <h3>Balanço para Cálculo do Momento</h3>
              <MathDiv>
                <BlockMath math={`c = \\dfrac{A - a_p}{2} + 0,15 \\cdot a_p`} />
                <BlockMath
                  math={`\\boxed{c = \\dfrac{${roundToDecimals(baseReturn.A)} - ${data.ap}}{2} + 0,15 \\cdot ${data.ap} = ${roundToDecimals(baseReturn.c)} \\text{ cm}}`}
                />
              </MathDiv>
              <h3>Excentricidade</h3>
              <MathDiv>
                <BlockMath
                  math={`\\boxed{e = \\dfrac{M_k}{N_k}= \\dfrac{${data.mk}}{${data.nk}} = ${roundToDecimals(baseReturn.e)} \\text{ cm}}`}
                />
                <BlockMath
                  math={`\\dfrac{A}{6} = \\dfrac{${roundToDecimals(baseReturn.A, 0)}}{6} = ${roundToDecimals(baseReturn.eccentricityLimit)} \\text{ cm}`}
                />
              </MathDiv>
              {extraReturn && (
                <>
                  {baseReturn.eccentricityWithinLimit ? (
                    <>
                      <h3>Cálculo das Tensões</h3>
                      <MathDiv>
                        <BlockMath
                          math={`\\sigma_{base} = \\dfrac{N_k}{A \\cdot B} = \\dfrac{${data.nk}}{${baseReturn.A} \\cdot ${baseReturn.B}} = ${extraReturn.sigmaBase.toFixed(2)} \\text{ kN/m}^2`}
                        />
                        <BlockMath
                          math={`\\boxed{\\sigma_{max} = \\sigma_{base} \\cdot \\left(1 + \\dfrac{6e}{A}\\right) = ${extraReturn.sigmaMax.toFixed(2)} \\text{ kN/m}^2}`}
                        />
                        <BlockMath
                          math={`\\boxed{\\sigma_{min} = \\sigma_{base} \\cdot \\left(1 - \\dfrac{6e}{A}\\right) = ${extraReturn.sigmaMin.toFixed(2)} \\text{ kN/m}^2}`}
                        />
                      </MathDiv>
                      {extraReturn.sigmaResist ? (
                        <>
                          <MathDiv>
                            <BlockMath math={`\\sigma_{max} \\leq \\sigma_{adm}`} />
                          </MathDiv>
                          <h3>Cálculo do Momento</h3>
                          <MathDiv>
                            <BlockMath math={`q_1 = ${extraReturn.q1.toFixed(2)} \\text{ kN/m}`} />
                            <BlockMath
                              math={`M_{max} = \\dfrac{q_1 \\cdot c^2}{2} + \\dfrac{\\left(q_{max} - q_1 \\right) \\cdot c}{2} \\cdot \\dfrac{2 \\cdot c}{3}`}
                            />
                            <BlockMath
                              math={`M_{max} = \\dfrac{${roundToDecimals(extraReturn.q1)} \\cdot ${roundToDecimals(baseReturn.c / 100)} ^2}{2} + \\dfrac{\\left(${roundToDecimals(extraReturn.sigmaMax)} - ${roundToDecimals(extraReturn.q1)} \\right) \\cdot ${roundToDecimals(baseReturn.c / 100)}}{2} \\cdot \\dfrac{2 \\cdot ${roundToDecimals(baseReturn.c / 100)}}{3}`}
                            />
                            <BlockMath math={`M_{max} = ${extraReturn.mMax.toFixed(2)} \\text{ kN.m}`} />
                            <BlockMath
                              math={`\\boxed{M_{sd} = M_{max} \\cdot \\gamma_f = ${extraReturn.mMax.toFixed(2)} \\cdot ${data.gammaf} = ${extraReturn.msd.toFixed(2)} \\text{ kN.m}}`}
                            />
                          </MathDiv>
                          <h3>Cálculo da Armadura</h3>
                          <MathDiv>
                            <BlockMath
                              math={`f_{yd} = \\dfrac{f_{yk}}{1.15} = \\dfrac{${data.fyk}}{1.15} = ${baseReturn.fyd.toFixed(2)} \\text{ MPa}`}
                            />
                            <BlockMath
                              math={`\\boxed{A_s = \\dfrac{M_{sd}}{0,85 \\cdot d \\cdot f_{yd}} = ${extraReturn.as.toFixed(2)} \\text{ cm}^2 \\text{/m}}`}
                            />
                          </MathDiv>
                        </>
                      ) : (
                        <>
                          <MathDiv>
                            <BlockMath math={`\\boxed{\\sigma_{max} > \\sigma_{adm}}`} />
                          </MathDiv>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <h3>Cálculo das Tensões</h3>
                      <MathDiv>
                        <BlockMath math={`x = 3 \\cdot \\left(\\dfrac{A}{2} - e\\right)`} />
                        <BlockMath
                          math={`x = 3 \\cdot \\left(\\dfrac{${data.A}}{2} - ${roundToDecimals(baseReturn.e)} \\right) = ${roundToDecimals(extraReturn.x)} \\text{ cm}`}
                        />
                        <BlockMath math={`\\boxed{\\sigma_{min} = 0 \\text{ kN/m}^2}`} />
                        <BlockMath
                          math={`\\sigma_{max} = \\dfrac{2 \\cdot N_k}{3 \\cdot B \\cdot \\left(\\dfrac{A}{2} - e\\right)}`}
                        />
                        <BlockMath
                          math={`\\boxed{\\sigma_{max} = \\dfrac{2 \\cdot ${roundToDecimals(baseReturn.totalVertical)} }{3 \\cdot ${data.B} \\cdot \\left(\\dfrac{ ${data.A} }{2} - ${roundToDecimals(baseReturn.e)}\\right)} = ${roundToDecimals(extraReturn.sigmaMax)} \\text{ kN/m}^2}`}
                        />
                      </MathDiv>
                      {extraReturn.sigmaResist ? (
                        <>
                          <MathDiv>
                            <BlockMath math={`\\boxed{\\sigma_{max} \\leq \\sigma_{adm}}`} />
                          </MathDiv>
                          <h3>Cálculo do Momento</h3>
                          <MathDiv>
                            <BlockMath math={`q_1 = \\sigma_{max} \\cdot \\left(\\dfrac{x-c}{x}\\right)`} />
                            <BlockMath
                              math={`q_1 = ${roundToDecimals(extraReturn.sigmaMax)} \\cdot \\left(\\dfrac{ ${roundToDecimals(extraReturn.x / 100)} - ${roundToDecimals(baseReturn.c / 100)} }{${roundToDecimals(extraReturn.x / 100)}}\\right) = ${roundToDecimals(extraReturn.q1)} \\text{ kN/m}`}
                            />
                            <BlockMath
                              math={`M_{max} = \\dfrac{q_1 \\cdot c^2}{2} + \\dfrac{c \\cdot \\left( q_{max} - q_1 \\right)}{2} \\cdot \\dfrac{2 \\cdot c}{3}`}
                            />
                            <BlockMath
                              math={`M_{max} = \\dfrac{${roundToDecimals(extraReturn.q1)} \\cdot ${roundToDecimals(baseReturn.c / 100)} ^2}{2} + \\dfrac{${roundToDecimals(baseReturn.c / 100)} \\cdot \\left( ${roundToDecimals(extraReturn.sigmaMax)} - ${roundToDecimals(extraReturn.q1)} \\right)}{2} \\cdot \\dfrac{2 \\cdot ${roundToDecimals(baseReturn.c / 100)}}{3}`}
                            />
                            <BlockMath
                              math={`M_{d} = M_{max} \\cdot \\gamma_f = ${roundToDecimals(extraReturn.mMax)} \\cdot ${data.gammaf} = ${roundToDecimals(extraReturn.msd)} \\text{ kN.m}`}
                            />
                            <BlockMath
                              math={`\\boxed{M_{d} = M_{max} \\cdot \\gamma_f = ${roundToDecimals(extraReturn.mMax)} \\cdot ${data.gammaf} = ${roundToDecimals(extraReturn.msd)} \\text{ kN.m}}`}
                            />
                          </MathDiv>
                          <h3>Cálculo da Armadura</h3>
                          <MathDiv>
                            <BlockMath
                              math={`f_{yd} = \\dfrac{f_{yk}}{1.15} = \\dfrac{${data.fyk}}{1.15} = ${baseReturn.fyd.toFixed(2)} \\text{ MPa}`}
                            />
                            <BlockMath
                              math={`\\boxed{A_s = \\dfrac{M_{sd}}{0,85 \\cdot d \\cdot f_{yd}} = ${extraReturn.as.toFixed(2)} \\text{ cm}^2 \\text{/m}}`}
                            />
                          </MathDiv>
                        </>
                      ) : (
                        <>
                          <MathDiv>
                            <BlockMath math={`\\sigma_{max} > \\sigma_{adm}`} />
                          </MathDiv>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
